import { useEffect, useState } from 'react';

import {
	attachClosestEdge,
	type Edge,
	extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
	draggable,
	dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { preserveOffsetOnSource } from '@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';

import type { TabDragState } from '../../types';

export function useDraggableTabs({
	isDragEnabled,
	isDropEnabled = true,
	position,
	id,
	handleDrag,
	isSelected,
	isOverflowing,
	isMoreMenu,
	elementRef,
}: {
	isDragEnabled: boolean;
	isDropEnabled?: boolean;
	position?: number;
	id?: string;
	handleDrag?: () => void;
	isSelected?: boolean;
	isMoreMenu?: boolean;
	isOverflowing?: boolean;
	elementRef: React.RefObject<HTMLElement>;
}) {
	const [isActiveDropZone, setIsActiveDropZone] = useState(false);
	const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
	const [dragState, setDragState] = useState<TabDragState>({ type: 'idle' });

	useEffect(() => {
		const element = elementRef.current;

		if (!element) {
			return;
		}

		// the more menu is configured differently,
		// and works as a single drop zone that cannot be dragged
		if (isMoreMenu) {
			return dropTargetForElements({
				element,
				canDrop: (args) => args.source.data.type === 'tab',
				getIsSticky: () => true,
				getData: () => ({
					id,
					position,
					isMoreMenu,
				}),
				onDragEnter: () => {
					setIsActiveDropZone(true);
				},
				onDragLeave: () => {
					setIsActiveDropZone(false);
				},
				onDrop: () => {
					setIsActiveDropZone(false);
				},
			});
		}
		return combine(
			draggable({
				element,
				onGenerateDragPreview({ location, nativeSetDragImage }) {
					setCustomNativeDragPreview({
						nativeSetDragImage,
						getOffset: preserveOffsetOnSource({
							element,
							input: location.current.input,
						}),
						render({ container }) {
							setDragState({ type: 'preview', container });
							return () => setDragState({ type: 'dragging' });
						},
					});
				},
				canDrag: () => isDragEnabled,
				onDragStart: handleDrag,
				onDrag: () => {
					setDragState({ type: 'dragging' });
				},
				onDrop: () => {
					setDragState({ type: 'idle' });
				},
				getInitialData() {
					return { position, id, type: 'tab', isOverflowing };
				},
			}),
			dropTargetForElements({
				element,
				canDrop: (args) => args.source.data.type === 'tab' && isDropEnabled,
				getIsSticky: () => true,
				getData: ({ input, element: dataElement }) => {
					const data = {
						id,
						position,
						isMoreMenu,
						isOverflowing,
					};
					return attachClosestEdge(data, {
						input,
						element: dataElement,
						allowedEdges: isOverflowing ? ['top', 'bottom'] : ['left', 'right'],
					});
				},
				onDrag({ source, self }) {
					if (source.data.id !== id) {
						setClosestEdge(extractClosestEdge(self.data));
					}
				},
				onDragLeave: () => {
					setClosestEdge(null);
				},
				onDrop: () => {
					setClosestEdge(null);
				},
			}),
		);
	}, [
		isDragEnabled,
		isOverflowing,
		position,
		id,
		isSelected,
		elementRef,
		handleDrag,
		isMoreMenu,
		isDropEnabled,
	]);
	const isDragging = Boolean(dragState && dragState.type === 'dragging');
	return { closestEdge, isActiveDropZone, dragState, isDragging };
}
